import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { petDoc } from '../db';
import { db } from '../firebase';
import { UserProfile, Screen } from '../App';
import PetCharacter, {
  CharacterConfig, DEFAULT_CONFIG,
  BODY_COLORS, CHEEK_COLORS, EYE_COLORS,
} from '../components/PetCharacter';

interface Props {
  user: UserProfile;
  navigate: (s: Screen) => void;
  mode?: 'create' | 'recreate';
  onComplete?: () => void;
}

const TOTAL_STEPS = 5; // 4 creation + 1 confirm
const PRESET_SEEN_KEY_PREFIX = 'minik-preset-seen';

const BODY_SHAPES: { id: CharacterConfig['bodyShape']; label: string; icon: string }[] = [
  { id: 'round', label: 'Yuvarlak', icon: '⬤' },
  { id: 'oval', label: 'Oval', icon: '🥚' },
  { id: 'squircle', label: 'Kare-yuvarlak', icon: '⬛' },
  { id: 'drop', label: 'Damla', icon: '💧' },
  { id: 'bean', label: 'Fasulye', icon: '◔' },
  { id: 'peanut', label: 'Çift form', icon: '∞' },
];
const BODY_SIZES: { id: CharacterConfig['bodySize']; label: string }[] = [
  { id: 'small', label: 'Küçük' },
  { id: 'medium', label: 'Orta' },
  { id: 'large', label: 'Büyük' },
];
const BODY_FINISHES: { id: CharacterConfig['bodyFinish']; label: string }[] = [
  { id: 'soft', label: 'Yumuşak' },
  { id: 'glossy', label: 'Parlak' },
  { id: 'velvet', label: 'Kadife' },
  { id: 'cosmic', label: 'Galaksi' },
  { id: 'glass', label: 'Cam' },
];
const BODY_PATTERNS: { id: CharacterConfig['bodyPattern']; label: string }[] = [
  { id: 'none', label: 'Sade' },
  { id: 'freckles', label: 'Benek' },
  { id: 'stars', label: 'Yıldız' },
  { id: 'waves', label: 'Dalga' },
  { id: 'heart', label: 'Kalp' },
];
const EYE_TYPES: { id: CharacterConfig['eyeType']; label: string }[] = [
  { id: 'big-round', label: 'Büyük' },
  { id: 'small-dot', label: 'Nokta' },
  { id: 'crescent', label: 'Ay' },
  { id: 'star', label: 'Yıldız' },
  { id: 'heart', label: 'Kalp' },
  { id: 'triangle', label: 'Üçgen' },
  { id: 'square', label: 'Kare' },
  { id: 'spiral', label: 'Spiral' },
  { id: 'glossy', label: 'Canlı' },
  { id: 'sleepy', label: 'Uykulu' },
  { id: 'diamond', label: 'Elmas' },
];
const MOUTH_TYPES: { id: CharacterConfig['mouthType']; label: string }[] = [
  { id: 'smile', label: 'Gülümseme' },
  { id: 'big-smile', label: 'Büyük Gülüş' },
  { id: 'small', label: 'Küçük' },
  { id: 'surprised', label: 'Şaşkın' },
  { id: 'tongue', label: 'Dil' },
  { id: 'sad', label: 'Üzgün' },
  { id: 'smirk', label: 'Muzip' },
  { id: 'uwu', label: 'Tatlı' },
];
const HEAD_ACCS: { id: CharacterConfig['headAccessory']; label: string; emoji: string }[] = [
  { id: 'none', label: 'Yok', emoji: '✖️' },
  { id: 'antenna', label: 'Anten', emoji: '📡' },
  { id: 'horn', label: 'Boynuz', emoji: '😈' },
  { id: 'ear', label: 'Kulak', emoji: '🐰' },
  { id: 'crown', label: 'Taç', emoji: '👑' },
  { id: 'hat', label: 'Şapka', emoji: '🎩' },
  { id: 'flower', label: 'Çiçek', emoji: '🌸' },
  { id: 'halo', label: 'Halo', emoji: '✨' },
  { id: 'bow', label: 'Fiyonk', emoji: '🎀' },
  { id: 'headphones', label: 'Kulaklık', emoji: '🎧' },
  { id: 'leaf', label: 'Yaprak', emoji: '🌿' },
];
const EXTRA_ACCS: { id: CharacterConfig['extraAccessory']; label: string; emoji: string }[] = [
  { id: 'none', label: 'Yok', emoji: '✖️' },
  { id: 'cape', label: 'Pelerin', emoji: '🦸' },
  { id: 'scarf', label: 'Fular', emoji: '🧣' },
  { id: 'wing', label: 'Kanat', emoji: '🦋' },
  { id: 'tail', label: 'Kuyruk', emoji: '🐱' },
  { id: 'satchel', label: 'Çanta', emoji: '👜' },
  { id: 'sparkle', label: 'Işıltı', emoji: '✨' },
  { id: 'float-heart', label: 'Kalp', emoji: '💗' },
];
const AURAS: { id: CharacterConfig['aura']; label: string }[] = [
  { id: 'none', label: 'Yok' },
  { id: 'glow', label: 'Işık' },
  { id: 'sparkles', label: 'Parıltı' },
  { id: 'hearts', label: 'Kalpler' },
  { id: 'orbit', label: 'Yörünge' },
];
const PERSONALITIES: { id: CharacterConfig['personality']; label: string; desc: string; emoji: string }[] = [
  { id: 'happy', label: 'Neşeli', desc: 'Enerjik ve çok emojili', emoji: '🥳' },
  { id: 'calm', label: 'Sakin', desc: 'Huzurlu ve nazik', emoji: '🌿' },
  { id: 'naughty', label: 'Yaramaz', desc: 'Ters ama sevimli', emoji: '😈' },
  { id: 'romantic', label: 'Romantik', desc: 'Aşk dolu mesajlar', emoji: '💕' },
];

const PRESETS: { label: string; desc: string; config: Partial<CharacterConfig> }[] = [
  {
    label: 'Ay Perisi',
    desc: 'Yumuşak, romantik, kanatlı',
    config: {
      bodyShape: 'drop', bodySize: 'medium', bodyColor: '#C77DFF',
      bodyFinish: 'glass', bodyPattern: 'heart', aura: 'hearts',
      eyeType: 'heart', eyeColor: '#BE123C', mouthType: 'smile', cheekColor: '#FFB3BA',
      headAccessory: 'flower', extraAccessory: 'wing', personality: 'romantic', name: 'Luna',
    },
  },
  {
    label: 'Uzay Minik',
    desc: 'Antenli, parlak, neşeli',
    config: {
      bodyShape: 'round', bodySize: 'medium', bodyColor: 'rainbow',
      bodyFinish: 'cosmic', bodyPattern: 'stars', aura: 'orbit',
      eyeType: 'star', eyeColor: '#1D4ED8', mouthType: 'big-smile', cheekColor: '#00BCD4',
      headAccessory: 'antenna', extraAccessory: 'cape', personality: 'happy', name: 'Nova',
    },
  },
  {
    label: 'Sakin Bulut',
    desc: 'Temiz, huzurlu, sade',
    config: {
      bodyShape: 'squircle', bodySize: 'large', bodyColor: '#EEEEEE',
      bodyFinish: 'soft', bodyPattern: 'waves', aura: 'glow',
      eyeType: 'crescent', eyeColor: '#0F766E', mouthType: 'small', cheekColor: '#C77DFF',
      headAccessory: 'none', extraAccessory: 'scarf', personality: 'calm', name: 'Momo',
    },
  },
  {
    label: 'Minik Kraliyet',
    desc: 'Taçlı ve gösterişli',
    config: {
      bodyShape: 'oval', bodySize: 'medium', bodyColor: '#E91E8C',
      bodyFinish: 'glossy', bodyPattern: 'freckles', aura: 'sparkles',
      eyeType: 'big-round', eyeColor: '#2E1065', mouthType: 'tongue', cheekColor: '#FFD700',
      headAccessory: 'crown', extraAccessory: 'tail', personality: 'naughty', name: 'Pufi',
    },
  },
  {
    label: 'Cam Kalp',
    desc: 'Şeffaf, duygusal, zarif',
    config: {
      bodyShape: 'bean', bodySize: 'medium', bodyColor: '#9C27B0',
      bodyFinish: 'glass', bodyPattern: 'heart', aura: 'glow',
      eyeType: 'glossy', eyeColor: '#2E1065', mouthType: 'uwu', cheekColor: '#FF6B9D',
      headAccessory: 'halo', extraAccessory: 'float-heart', personality: 'romantic', name: 'Mira',
    },
  },
  {
    label: 'Müzik Minik',
    desc: 'Kulaklıklı, muzip, şehirli',
    config: {
      bodyShape: 'peanut', bodySize: 'medium', bodyColor: '#00BCD4',
      bodyFinish: 'velvet', bodyPattern: 'freckles', aura: 'sparkles',
      eyeType: 'diamond', eyeColor: '#111827', mouthType: 'smirk', cheekColor: '#FFD700',
      headAccessory: 'headphones', extraAccessory: 'satchel', personality: 'naughty', name: 'Bibi',
    },
  },
];

const stepSlide = {
  enter: { opacity: 0, x: 28 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -28 },
};

const bodyShapeLabel = (id: CharacterConfig['bodyShape']) =>
  BODY_SHAPES.find(item => item.id === id)?.label ?? 'Gövde';
const finishLabel = (id: CharacterConfig['bodyFinish']) =>
  BODY_FINISHES.find(item => item.id === id)?.label ?? 'Yüzey';
const patternLabel = (id: CharacterConfig['bodyPattern']) =>
  BODY_PATTERNS.find(item => item.id === id)?.label ?? 'Desen';
const eyeLabel = (id: CharacterConfig['eyeType']) =>
  EYE_TYPES.find(item => item.id === id)?.label ?? 'Göz';
const headLabel = (id: CharacterConfig['headAccessory']) =>
  HEAD_ACCS.find(item => item.id === id)?.label ?? 'Baş';
const auraLabel = (id: CharacterConfig['aura']) =>
  AURAS.find(item => item.id === id)?.label ?? 'Aura';

export default function CharacterCreator({ user, navigate, mode = 'create', onComplete }: Props) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [cfg, setCfg] = useState<CharacterConfig>({ ...DEFAULT_CONFIG });

  const set = <K extends keyof CharacterConfig>(k: K, v: CharacterConfig[K]) =>
    setCfg(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    let active = true;
    getDoc(petDoc(user.coupleId)).then(snap => {
      const current = snap.exists() ? snap.data().characterConfig as CharacterConfig | undefined : undefined;
      if (active && current) setCfg({ ...DEFAULT_CONFIG, ...current });
    }).catch(() => {});
    return () => { active = false; };
  }, [user.coupleId]);

  useEffect(() => {
    if (mode !== 'create') return;

    const storageKey = `${PRESET_SEEN_KEY_PREFIX}:${user.uid}`;
    const locallySeen = (() => {
      try {
        return window.localStorage.getItem(storageKey) === '1';
      } catch {
        return false;
      }
    })();

    if (user.seenMinikPresets || locallySeen) return;

    setShowPresets(true);
    try {
      window.localStorage.setItem(storageKey, '1');
    } catch {}
    setDoc(doc(db, 'users', user.uid), { seenMinikPresets: true }, { merge: true }).catch(() => {});
  }, [mode, user.seenMinikPresets, user.uid]);

  const next = () => setStep(s => s + 1);
  const back = () => setStep(s => Math.max(1, s - 1));
  const pick = <T,>(items: T[]) => items[Math.floor(Math.random() * items.length)];
  const randomize = () => {
    setCfg(prev => ({
      ...prev,
      bodyShape: pick(BODY_SHAPES).id,
      bodySize: pick(BODY_SIZES).id,
      bodyColor: pick(Object.values(BODY_COLORS)),
      bodyFinish: pick(BODY_FINISHES).id,
      bodyPattern: pick(BODY_PATTERNS).id,
      aura: pick(AURAS).id,
      eyeType: pick(EYE_TYPES).id,
      eyeColor: pick(EYE_COLORS),
      mouthType: pick(MOUTH_TYPES).id,
      cheekColor: pick([...CHEEK_COLORS, null]),
      headAccessory: pick(HEAD_ACCS).id,
      extraAccessory: pick(EXTRA_ACCS).id,
      personality: pick(PERSONALITIES).id,
    }));
  };
  const applyPreset = (preset: typeof PRESETS[number]) => {
    setCfg(prev => ({ ...prev, ...preset.config } as CharacterConfig));
    setShowPresets(false);
  };

  const save = async () => {
    setSaving(true);
    const baseDoc = petDoc(user.coupleId);
    const snap = await getDoc(baseDoc);
    if (!snap.exists()) {
      await setDoc(baseDoc, {
        happiness: 70, cleanliness: 70, hunger: 70,
        bond: 18,
        totalDays: 0, level: 0, xp: 0,
        lastFed: serverTimestamp(), lastWashed: serverTimestamp(),
        lastPlayed: serverTimestamp(), lastActive: serverTimestamp(),
        lastCareAt: serverTimestamp(), lastCareDecayAt: serverTimestamp(), lastBondAt: serverTimestamp(),
        lastBondDecayDate: new Date().toISOString().slice(0, 10),
        bondDate: new Date().toISOString().slice(0, 10),
        dailyBondGain: 0,
        xpDate: new Date().toISOString().slice(0, 10),
        dailyCareXp: 0, dailyCareCount: 0,
        levelUpDate: serverTimestamp(), showLevelUp: false,
        characterConfig: cfg, createdAt: serverTimestamp(),
      });
    } else {
      await setDoc(baseDoc, { characterConfig: cfg, createdAt: serverTimestamp() }, { merge: true });
    }
    setSaving(false);
    if (onComplete) onComplete();
    else navigate('pet');
  };

  // Recreate warning gate
  if (showWarning) {
    return (
      <div style={S.fullPage}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={S.warningCard}
        >
          <span style={{ fontSize: 52 }}>⚠️</span>
          <h2 style={S.warnTitle}>Emin misiniz?</h2>
          <p style={S.warnText}>
            Mevcut karakterin tüm geçmişi ve doğum günü sıfırlanacak.
            Bu işlem geri alınamaz.
          </p>
          <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 8 }}>
            <motion.button whileTap={{ scale: 0.96 }} style={S.ghostBtn}
              onClick={() => navigate('settings')}>
              Vazgeç
            </motion.button>
            <motion.button whileTap={{ scale: 0.96 }}
              style={{ ...S.primaryBtn, background: 'linear-gradient(135deg, #c62828, #e53935)', flex: 1 }}
              onClick={() => setShowWarning(false)}>
              Yeniden Oluştur
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={S.container}>
      {/* Progress */}
      <div style={S.progressBar}>
        <motion.div
          animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={S.progressFill}
        />
      </div>

      {/* Step dots */}
      <div style={S.stepDots}>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <motion.div key={i} animate={{
            background: i < step ? '#E91E8C' : 'rgba(255,255,255,0.15)',
            scale: i === step - 1 ? 1.3 : 1,
          }} style={S.dot} />
        ))}
      </div>

      <div style={S.previewPanel}>
        <div style={S.previewHeader}>
          <div>
            <p style={S.previewKicker}>Canlı önizleme</p>
            <p style={S.previewSub}>Seçtikçe karakter burada güncellenir.</p>
          </div>
          <div style={S.previewActions}>
            <button type="button" style={S.previewGhostBtn} onClick={randomize}>Karıştır</button>
            <button type="button" style={S.previewGhostBtn} onClick={() => setCfg({ ...DEFAULT_CONFIG })}>Sıfırla</button>
          </div>
        </div>
        <div style={S.preview}>
          <motion.div
            key={JSON.stringify(cfg)}
            initial={{ scale: 0.85 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 350, damping: 22 }}
          >
            <PetCharacter config={cfg} size={112} />
          </motion.div>
          <div style={S.previewSummary}>
            <span>{cfg.name || 'İsimsiz'}</span>
            <small>
              {bodyShapeLabel(cfg.bodyShape)}
              {' · '}
              {cfg.personality === 'happy' ? 'Neşeli' : cfg.personality === 'calm' ? 'Sakin' : cfg.personality === 'naughty' ? 'Yaramaz' : 'Romantik'}
            </small>
          </div>
        </div>
        <div style={S.summaryChipRow}>
          <span style={S.summaryChip}>{bodyShapeLabel(cfg.bodyShape)} gövde</span>
          <span style={S.summaryChip}>{finishLabel(cfg.bodyFinish)} yüzey</span>
          <span style={S.summaryChip}>{patternLabel(cfg.bodyPattern)} desen</span>
          <span style={S.summaryChip}>{eyeLabel(cfg.eyeType)} göz</span>
          <span style={S.summaryChip}>{headLabel(cfg.headAccessory)}</span>
          <span style={S.summaryChip}>{auraLabel(cfg.aura)} aura</span>
        </div>
      </div>

      {showPresets && (
      <div style={S.presetPanel}>
        <div style={S.presetHead}>
          <div>
            <p style={S.previewKicker}>Hazır stiller</p>
            <p style={S.previewSub}>İstersen buradan başlayıp üstüne oynayabilirsin.</p>
          </div>
          <button type="button" style={S.presetSkipBtn} onClick={() => setShowPresets(false)}>
            Geç
          </button>
        </div>
        <div style={S.presetGrid}>
          {PRESETS.map(preset => (
            <button
              key={preset.label}
              type="button"
              style={S.presetButton}
              onClick={() => applyPreset(preset)}
            >
              <PetCharacter config={{ ...cfg, ...preset.config } as CharacterConfig} size={44} />
              <span>{preset.label}</span>
              <small>{preset.desc}</small>
            </button>
          ))}
        </div>
      </div>
      )}

      <AnimatePresence mode="wait">
        {/* STEP 1 — Body */}
        {step === 1 && (
          <motion.div key="step1" variants={stepSlide} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.22 }} style={S.stepContent}>
            <h2 style={S.stepTitle}>Vücut</h2>
            <p style={S.stepSub}>Şekil, boyut ve renk seç</p>

            <div style={S.sectionLabel}>Şekil</div>
            <div style={S.optionGrid4}>
              {BODY_SHAPES.map(s => (
                <motion.button key={s.id} whileTap={{ scale: 0.9 }}
                  style={{ ...S.optBtn, ...(cfg.bodyShape === s.id ? S.optBtnActive : {}) }}
                  onClick={() => set('bodyShape', s.id)}>
                  <span style={{ fontSize: 22 }}>{s.icon}</span>
                  <span style={{ fontSize: 11 }}>{s.label}</span>
                </motion.button>
              ))}
            </div>

            <div style={S.sectionLabel}>Boyut</div>
            <div style={S.optionRow3}>
              {BODY_SIZES.map(s => (
                <motion.button key={s.id} whileTap={{ scale: 0.92 }}
                  style={{ ...S.sizeBtn, ...(cfg.bodySize === s.id ? S.sizeBtnActive : {}) }}
                  onClick={() => set('bodySize', s.id)}>
                  {s.label}
                </motion.button>
              ))}
            </div>

            <div style={S.sectionLabel}>Yüzey</div>
            <div style={S.optionGrid5}>
              {BODY_FINISHES.map(f => (
                <motion.button key={f.id} whileTap={{ scale: 0.92 }}
                  style={{ ...S.compactBtn, ...(cfg.bodyFinish === f.id ? S.optBtnActive : {}) }}
                  onClick={() => set('bodyFinish', f.id)}>
                  {f.label}
                </motion.button>
              ))}
            </div>

            <div style={S.sectionLabel}>Desen</div>
            <div style={S.optionGrid5}>
              {BODY_PATTERNS.map(p => (
                <motion.button key={p.id} whileTap={{ scale: 0.92 }}
                  style={{ ...S.compactBtn, ...(cfg.bodyPattern === p.id ? S.optBtnActive : {}) }}
                  onClick={() => set('bodyPattern', p.id)}>
                  {p.label}
                </motion.button>
              ))}
            </div>

            <div style={S.sectionLabel}>Renk</div>
            <div style={S.colorRow}>
              {Object.entries(BODY_COLORS).map(([key, hex]) => (
                <motion.button key={key} whileTap={{ scale: 0.85 }}
                  onClick={() => set('bodyColor', hex === 'rainbow' ? 'rainbow' : hex)}
                  style={{
                    ...S.colorDot,
                    background: hex === 'rainbow'
                      ? 'linear-gradient(135deg, #FF6B9D, #FFD700, #7dff8f, #00E5FF)'
                      : hex,
                    outline: (cfg.bodyColor === hex || (hex === 'rainbow' && cfg.bodyColor === 'rainbow'))
                      ? '3px solid white' : 'none',
                    outlineOffset: 2,
                  }}
                />
              ))}
            </div>

            <motion.button whileTap={{ scale: 0.96 }} style={S.primaryBtn} onClick={next}>
              Devam →
            </motion.button>
          </motion.div>
        )}

        {/* STEP 2 — Face */}
        {step === 2 && (
          <motion.div key="step2" variants={stepSlide} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.22 }} style={S.stepContent}>
            <h2 style={S.stepTitle}>Yüz</h2>
            <p style={S.stepSub}>Göz, ağız ve yanak</p>

            <div style={S.sectionLabel}>Gözler</div>
            <div style={S.optionGrid4}>
              {EYE_TYPES.map(e => (
                <motion.button key={e.id} whileTap={{ scale: 0.9 }}
                  style={{ ...S.optBtn, ...(cfg.eyeType === e.id ? S.optBtnActive : {}) }}
                  onClick={() => set('eyeType', e.id)}>
                  <PetCharacter config={{ ...cfg, eyeType: e.id }} size={36} />
                  <span style={{ fontSize: 10 }}>{e.label}</span>
                </motion.button>
              ))}
            </div>

            <div style={S.sectionLabel}>Göz Rengi</div>
            <div style={S.colorRow}>
              {EYE_COLORS.map(c => (
                <motion.button key={c} whileTap={{ scale: 0.85 }}
                  onClick={() => set('eyeColor', c)}
                  style={{
                    ...S.colorDot,
                    background: c,
                    outline: cfg.eyeColor === c ? '3px solid white' : 'none',
                    outlineOffset: 2,
                  }} />
              ))}
            </div>

            <div style={S.sectionLabel}>Ağız</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, width: '100%' }}>
              {MOUTH_TYPES.map(m => (
                <motion.button key={m.id} whileTap={{ scale: 0.9 }}
                  style={{ ...S.optBtn, ...(cfg.mouthType === m.id ? S.optBtnActive : {}) }}
                  onClick={() => set('mouthType', m.id)}>
                  <PetCharacter config={{ ...cfg, mouthType: m.id }} size={36} />
                  <span style={{ fontSize: 10 }}>{m.label}</span>
                </motion.button>
              ))}
            </div>

            <div style={S.sectionLabel}>Yanak</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%', flexWrap: 'wrap' as const }}>
              <motion.button whileTap={{ scale: 0.9 }}
                style={{
                  ...S.colorDot, background: 'rgba(255,255,255,0.12)',
                  outline: cfg.cheekColor === null ? '3px solid white' : 'none', outlineOffset: 2,
                }}
                onClick={() => set('cheekColor', null)}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>✖</span>
              </motion.button>
              {CHEEK_COLORS.map(c => (
                <motion.button key={c} whileTap={{ scale: 0.85 }}
                  onClick={() => set('cheekColor', c)}
                  style={{
                    ...S.colorDot, background: c,
                    outline: cfg.cheekColor === c ? '3px solid white' : 'none', outlineOffset: 2,
                  }} />
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <motion.button whileTap={{ scale: 0.96 }} style={S.ghostBtn} onClick={back}>← Geri</motion.button>
              <motion.button whileTap={{ scale: 0.96 }} style={{ ...S.primaryBtn, flex: 1 }} onClick={next}>Devam →</motion.button>
            </div>
          </motion.div>
        )}

        {/* STEP 3 — Accessories */}
        {step === 3 && (
          <motion.div key="step3" variants={stepSlide} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.22 }} style={S.stepContent}>
            <h2 style={S.stepTitle}>Aksesuar</h2>
            <p style={S.stepSub}>Başlık ve ekstra</p>

            <div style={S.sectionLabel}>Baş</div>
            <div style={S.optionGrid4}>
              {HEAD_ACCS.map(a => (
                <motion.button key={a.id} whileTap={{ scale: 0.9 }}
                  style={{ ...S.optBtn, ...(cfg.headAccessory === a.id ? S.optBtnActive : {}) }}
                  onClick={() => set('headAccessory', a.id)}>
                  <span style={{ fontSize: 22 }}>{a.emoji}</span>
                  <span style={{ fontSize: 10 }}>{a.label}</span>
                </motion.button>
              ))}
            </div>

            <div style={S.sectionLabel}>Ekstra</div>
            <div style={S.optionGrid4}>
              {EXTRA_ACCS.map(a => (
                <motion.button key={a.id} whileTap={{ scale: 0.9 }}
                  style={{ ...S.optBtn, ...(cfg.extraAccessory === a.id ? S.optBtnActive : {}) }}
                  onClick={() => set('extraAccessory', a.id)}>
                  <span style={{ fontSize: 20 }}>{a.emoji}</span>
                  <span style={{ fontSize: 10 }}>{a.label}</span>
                </motion.button>
              ))}
            </div>

            <div style={S.sectionLabel}>Aura</div>
            <div style={S.optionGrid5}>
              {AURAS.map(a => (
                <motion.button key={a.id} whileTap={{ scale: 0.92 }}
                  style={{ ...S.compactBtn, ...(cfg.aura === a.id ? S.optBtnActive : {}) }}
                  onClick={() => set('aura', a.id)}>
                  {a.label}
                </motion.button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <motion.button whileTap={{ scale: 0.96 }} style={S.ghostBtn} onClick={back}>← Geri</motion.button>
              <motion.button whileTap={{ scale: 0.96 }} style={{ ...S.primaryBtn, flex: 1 }} onClick={next}>Devam →</motion.button>
            </div>
          </motion.div>
        )}

        {/* STEP 4 — Name & Personality */}
        {step === 4 && (
          <motion.div key="step4" variants={stepSlide} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.22 }} style={S.stepContent}>
            <h2 style={S.stepTitle}>İsim & Kişilik</h2>
            <p style={S.stepSub}>Son dokunuşlar</p>

            <div style={S.sectionLabel}>Karakter İsmi</div>
            <input
              value={cfg.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Karakterine bir isim ver"
              maxLength={10}
              style={S.nameInput}
            />

            <div style={S.sectionLabel}>Kişilik</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
              {PERSONALITIES.map(p => (
                <motion.button key={p.id} whileTap={{ scale: 0.97 }}
                  onClick={() => set('personality', p.id)}
                  style={{
                    ...S.personalityBtn,
                    background: cfg.personality === p.id
                      ? 'linear-gradient(135deg, rgba(233,30,140,0.18), rgba(156,39,176,0.18))'
                      : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${cfg.personality === p.id ? 'rgba(233,30,140,0.5)' : 'rgba(255,255,255,0.09)'}`,
                  }}>
                  <span style={{ fontSize: 24 }}>{p.emoji}</span>
                  <div style={{ flex: 1, textAlign: 'left' as const }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: 'white', margin: 0 }}>{p.label}</p>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', margin: 0 }}>{p.desc}</p>
                  </div>
                  {cfg.personality === p.id && <span style={{ fontSize: 16 }}>✓</span>}
                </motion.button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <motion.button whileTap={{ scale: 0.96 }} style={S.ghostBtn} onClick={back}>← Geri</motion.button>
              <motion.button whileTap={{ scale: 0.96 }} style={{ ...S.primaryBtn, flex: 1 }}
                onClick={next} disabled={!cfg.name.trim()}>
                Görüntüle →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* STEP 5 — Confirmation */}
        {step === 5 && (
          <motion.div key="step5" variants={stepSlide} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.22 }} style={{ ...S.stepContent, textAlign: 'center' as const }}>

            <motion.span style={{ fontSize: 18, color: 'rgba(255,255,255,0.5)' }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              Karakteriniz hazır!
            </motion.span>

            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 16, delay: 0.15 }}
              style={S.bigPreview}
            >
              <div style={S.bigGlow} />
              <PetCharacter config={cfg} size={160} />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              style={S.confirmName}
            >
              Tanışın, bu bizim {cfg.name}!
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              style={S.stepSub}
            >
              {cfg.personality === 'happy' && 'Enerjisi hiç bitmez, her zaman neşeli!'}
              {cfg.personality === 'calm' && 'Sakin ve huzurlu, size iyi gelecek.'}
              {cfg.personality === 'naughty' && 'Biraz yaramaz ama sonunda sever sizi!'}
              {cfg.personality === 'romantic' && 'Aşk dolu mesajlar göndereceğini söylüyor.'}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              style={{ display: 'flex', gap: 10, width: '100%' }}
            >
              <motion.button whileTap={{ scale: 0.96 }} style={S.ghostBtn} onClick={back}>
                Düzenle
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.96 }}
                style={{ ...S.primaryBtn, flex: 1 }}
                onClick={save}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : 'Haydi Başlayalım! 🎉'}
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '0 0 40px', position: 'relative',
  },
  fullPage: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  progressBar: { width: '100%', height: 3, background: 'rgba(255,255,255,0.08)', flexShrink: 0 },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #E91E8C, #C77DFF)', borderRadius: '0 2px 2px 0' },
  stepDots: { display: 'flex', gap: 8, padding: '16px 0 8px' },
  dot: { width: 8, height: 8, borderRadius: '50%' },
  previewPanel: {
    width: 'calc(100% - 32px)',
    maxWidth: 420,
    margin: '0 16px 8px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 24,
    padding: 14,
    boxShadow: '0 20px 70px rgba(0,0,0,0.22)',
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 10,
  },
  previewKicker: { color: '#BF5AF2', fontSize: 12, fontWeight: 850, margin: 0, lineHeight: 1 },
  previewSub: { color: 'rgba(255,255,255,0.48)', fontSize: 12, margin: '6px 0 0', lineHeight: 1.4 },
  previewActions: { display: 'flex', gap: 8, flexShrink: 0 },
  previewGhostBtn: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.9)',
    borderRadius: 999,
    minHeight: 34,
    padding: '0 12px',
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
  },
  preview: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    padding: '8px 0 4px',
  },
  previewSummary: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    textAlign: 'center',
  },
  previewSummarySpan: {},
  previewSummarySmall: { fontSize: 12, color: 'rgba(255,255,255,0.56)' },
  presetPanel: {
    width: 'calc(100% - 32px)',
    maxWidth: 420,
    margin: '0 16px 8px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 22,
    padding: 12,
  },
  presetHead: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 10,
  },
  presetSkipBtn: {
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.07)',
    color: 'rgba(255,255,255,0.86)',
    borderRadius: 999,
    minHeight: 30,
    padding: '0 12px',
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
    flexShrink: 0,
  },
  presetGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
  presetButton: {
    appearance: 'none',
    WebkitAppearance: 'none',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(0,0,0,0.16)',
    color: 'white',
    borderRadius: 18,
    minHeight: 118,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    cursor: 'pointer',
    textAlign: 'center',
  },
  stepContent: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 14, padding: '8px 20px 0', width: '100%', maxWidth: 420,
  },
  stepTitle: {
    fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, margin: 0,
    background: 'linear-gradient(135deg, #FF6B9D, #C77DFF)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  stepSub: { color: 'rgba(255,255,255,0.45)', fontSize: 13, margin: 0, textAlign: 'center' as const },
  sectionLabel: {
    fontSize: 10, color: 'rgba(255,255,255,0.35)',
    textTransform: 'uppercase' as const, letterSpacing: 1,
    width: '100%', paddingLeft: 2,
  },
  optionGrid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, width: '100%' },
  optionGrid5: { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, width: '100%' },
  optionRow3: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, width: '100%' },
  optBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '10px 4px', borderRadius: 12, cursor: 'pointer',
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.7)', transition: 'all 0.15s',
  },
  compactBtn: {
    minHeight: 42,
    padding: '8px 4px',
    borderRadius: 12,
    cursor: 'pointer',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.72)',
    fontSize: 11,
    fontWeight: 800,
    transition: 'all 0.15s',
  },
  optBtnActive: {
    background: 'rgba(233,30,140,0.18)', border: '1px solid rgba(233,30,140,0.5)',
    color: 'white',
  },
  sizeBtn: {
    padding: '10px 0', borderRadius: 12, cursor: 'pointer',
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600,
    transition: 'all 0.15s',
  },
  sizeBtnActive: {
    background: 'rgba(233,30,140,0.18)', border: '1px solid rgba(233,30,140,0.5)', color: 'white',
  },
  colorRow: { display: 'flex', gap: 8, flexWrap: 'wrap' as const, width: '100%' },
  colorDot: {
    width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  summaryChipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
  },
  summaryChip: {
    borderRadius: 999,
    padding: '8px 10px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(235,235,245,0.82)',
    fontSize: 12,
    fontWeight: 800,
  },
  nameInput: {
    width: '100%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 14, padding: '13px 16px', color: 'white', fontSize: 16, outline: 'none',
    boxSizing: 'border-box' as const, textAlign: 'center' as const,
    fontFamily: "'Playfair Display', serif",
  },
  personalityBtn: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 16px', borderRadius: 16, cursor: 'pointer', width: '100%',
    transition: 'all 0.15s',
  },
  bigPreview: {
    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px 0 10px',
  },
  bigGlow: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at 50% 60%, rgba(233,30,140,0.25) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  confirmName: {
    fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, margin: 0,
    background: 'linear-gradient(135deg, #FF6B9D, #C77DFF)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  primaryBtn: {
    width: '100%', padding: '15px 24px',
    background: 'linear-gradient(135deg, #E91E8C, #9C27B0)',
    border: 'none', borderRadius: 20,
    color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 6px 24px rgba(233,30,140,0.3)',
  },
  ghostBtn: {
    padding: '15px 20px', minWidth: 80,
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20, color: 'rgba(255,255,255,0.6)', fontSize: 14, cursor: 'pointer',
  },
  warningCard: {
    background: 'rgba(30,10,50,0.95)', border: '1px solid rgba(200,50,50,0.4)',
    borderRadius: 24, padding: '32px 28px', maxWidth: 380, width: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
    textAlign: 'center' as const,
  },
  warnTitle: {
    fontFamily: "'Playfair Display', serif", fontSize: 22, margin: 0,
    color: '#ff8a80',
  },
  warnText: { fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, margin: 0 },
};
